import { useRef } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { MAX_LOGO_DATA_URL_LENGTH } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { formatCnpj } from "@/lib/cnpj";
import { orgTypeMeta } from "@/components/organization/org-type-meta";

/** Logo, nome e natureza da organização no topo de Dados — o equivalente da foto em Minha conta.
 *  O logo salva na hora, como a foto; é o que sai no recibo e nas etiquetas quando o laboratório
 *  não tem um logo próprio no perfil. */
export function CabecalhoDaOrganizacao({ canManage }: { canManage: boolean }) {
  const utils = trpc.useUtils();
  const inputRef = useRef<HTMLInputElement>(null);
  const perfil = trpc.organization.getProfiles.useQuery().data;

  const salvar = trpc.organization.updateLogo.useMutation({
    onSuccess: (_, { logoUrl }) => {
      toast.success(logoUrl ? "Logo atualizado." : "Logo removido.");
      utils.organization.getProfiles.invalidate();
      utils.organization.listMine.invalidate();
      utils.me.current.invalidate();
    },
    onError: (e) => toast.error(e.message || "Não foi possível salvar o logo."),
  });

  const escolher = (file: File) => {
    if (!file.type.startsWith("image/")) return toast.error("Selecione um arquivo de imagem.");
    if (file.size > 500_000) return toast.error("Imagem muito grande (máx. ~500 KB).");
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (dataUrl.length > MAX_LOGO_DATA_URL_LENGTH) return toast.error("Imagem muito grande (máx. ~500 KB).");
      salvar.mutate({ logoUrl: dataUrl });
    };
    reader.onerror = () => toast.error("Não foi possível ler o arquivo.");
    reader.readAsDataURL(file);
  };

  if (!perfil) return null;
  const meta = orgTypeMeta({ isPersonal: perfil.isPersonal, types: perfil.types });
  const Icone = meta.icon;
  const logo = perfil.logoUrl as string | null;

  return (
    <div data-tour="config-organizacao-logo" className="flex items-center gap-4">
      <div className="group relative h-20 w-20 shrink-0">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
          {logo ? (
            <img src={logo} alt={`Logo de ${perfil.name}`} className="h-full w-full object-contain p-1.5" />
          ) : (
            <Icone className="h-8 w-8" style={{ color: meta.color }} />
          )}
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={salvar.isPending}
            aria-label={logo ? "Trocar logo" : "Enviar logo"}
            className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/0 text-transparent transition-colors hover:bg-black/50 hover:text-white focus-visible:bg-black/50 focus-visible:text-white disabled:cursor-wait"
          >
            {salvar.isPending ? <Loader2 className="h-6 w-6 animate-spin text-white" /> : <Camera className="h-6 w-6" />}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) escolher(file);
            e.target.value = "";
          }}
        />
      </div>

      <div className="min-w-0">
        <p className="truncate text-base font-semibold text-zinc-900 dark:text-foreground">{perfil.name}</p>
        <p className="truncate text-sm text-zinc-500 dark:text-muted-foreground">
          {meta.label}
          {perfil.document ? ` · CNPJ ${formatCnpj(perfil.document)}` : ""}
        </p>
        {canManage && (
          <div className="mt-1 flex items-center gap-3 text-xs">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={salvar.isPending} className="font-medium text-brand-700 hover:underline dark:text-primary">
              {logo ? "Trocar logo" : "Enviar logo"}
            </button>
            {logo && (
              <button
                type="button"
                onClick={() => salvar.mutate({ logoUrl: null })}
                disabled={salvar.isPending}
                className="inline-flex items-center gap-1 text-zinc-500 transition-colors hover:text-red-600"
              >
                <Trash2 className="h-3 w-3" />
                Remover
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
